import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { EditUserDialogProps } from '../types';

export function EditUserDialog({
  isDark,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSave,
  isSaving = false,
}: EditUserDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Редактировать пользователя</DialogTitle>
          <DialogDescription className={cn(
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>
            Обновить данные пользователя
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, email: e.target.value }))}
              className={cn(
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-white' 
                  : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="username" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Логин</Label>
            <Input
              id="username"
              type="text"
              value={formData.username || ''}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, username: e.target.value }))}
              className={cn(
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-white' 
                  : 'bg-white border-slate-200'
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>Имя</Label>
              <Input
                id="firstName"
                type="text"
                value={formData.firstName || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, firstName: e.target.value }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>Фамилия</Label>
              <Input
                id="lastName"
                type="text"
                value={formData.lastName || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, lastName: e.target.value }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="role" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Роль</Label>
            <Select
              value={formData.role}
              onValueChange={(value: 'user' | 'admin') => onFormDataChange(prev => ({ ...prev, role: value }))}
            >
              <SelectTrigger className={cn(
                isDark 
                  ? 'bg-slate-800 border-slate-700 text-white' 
                  : 'bg-white border-slate-200'
              )}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Пользователь</SelectItem>
                <SelectItem value="admin">Админ</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isSubscribed"
              checked={formData.isSubscribed || false}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, isSubscribed: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300"
            />
            <Label htmlFor="isSubscribed" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Подписка</Label>
          </div>

          {formData.isSubscribed && (
            <div className="space-y-2">
              <Label htmlFor="subscribedEnd" className={cn(
                isDark ? 'text-slate-300' : 'text-slate-700'
              )}>Действует до *</Label>
              <Input
                id="subscribedEnd"
                type="datetime-local"
                value={formData.subscribedEnd ?? ''}
                onChange={(e) => onFormDataChange(prev => ({
                  ...prev,
                  subscribedEnd: e.target.value || null,
                }))}
                className={cn(
                  isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200'
                )}
              />
              <p className={cn('text-xs', isDark ? 'text-slate-500' : 'text-slate-400')}>
                Без даты приложение не считает подписку активной, а крон истечения её не видит
              </p>
            </div>
          )}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isBlocked"
              checked={formData.isBlocked || false}
              onChange={(e) => onFormDataChange(prev => ({ ...prev, isBlocked: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300"
            />
            <Label htmlFor="isBlocked" className={cn(
              isDark ? 'text-slate-300' : 'text-slate-700'
            )}>Заблокирован (blacklist)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
            className={cn(
              isDark
                ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                : 'border-slate-200'
            )}
          >
            Отмена
          </Button>
          <Button onClick={onSave} disabled={isSaving} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
